import { onCLS, onINP, onLCP } from "web-vitals";

declare global {
  interface Window {
    dataLayer: any[];
  }
}

// Ensure dataLayer exists
window.dataLayer = window.dataLayer || [];

export const Analytics = {
  push: (event: string, data: Record<string, any> = {}) => {
    window.dataLayer.push({
      event,
      ...data,
      timestamp: new Date().toISOString(),
    });
  },

  pageView: (path: string, title: string) => {
    window.dataLayer.push({
      event: "page_view",
      page_path: path,
      page_title: title,
    });
  },

  // E-commerce Events
  viewItem: (item: any) => {
    window.dataLayer.push({
      event: "view_item",
      ecommerce: {
        currency: "USD", // Display currency
        value: item.ticket_price,
        items: [
          {
            item_id: item.id,
            item_name: item.title,
            price: item.ticket_price,
            quantity: 1,
          },
        ],
      },
    });
  },

  addToCart: (item: any, quantity: number, totalValue: number) => {
    window.dataLayer.push({
      event: "add_to_cart",
      ecommerce: {
        currency: "USD",
        value: totalValue,
        items: [
          {
            item_id: item.id,
            item_name: item.title,
            price: item.ticket_price, // Unit price (might vary with bundles, but keeping simple)
            quantity: quantity,
          },
        ],
      },
    });
  },

  beginCheckout: (item: any, quantity: number, totalValue: number) => {
    window.dataLayer.push({
      event: "begin_checkout",
      ecommerce: {
        currency: "USD",
        value: totalValue,
        items: [
          {
            item_id: item.id,
            item_name: item.title,
            quantity: quantity,
          },
        ],
      },
    });
  },

  purchase: (
    transactionId: string,
    item: any,
    quantity: number,
    totalValue: number
  ) => {
    window.dataLayer.push({
      event: "purchase",
      ecommerce: {
        transaction_id: transactionId,
        value: totalValue,
        currency: "USD",
        items: [
          {
            item_id: item.id,
            item_name: item.title,
            quantity: quantity,
          },
        ],
      },
    });
  },

  // Web Vitals
  initWebVitals: () => {
    const sendToGTM = (metric: any) => {
      Analytics.push("core_web_vitals", {
        metric_name: metric.name,
        metric_value: metric.value,
        metric_delta: metric.delta,
        metric_id: metric.id,
      });
      // console.log(`[Vitals] ${metric.name}:`, metric.value);
    };

    onCLS(sendToGTM);
    onINP(sendToGTM);
    onLCP(sendToGTM);
  },

  // Campaign & UTM Tracking (Fix for Unassigned)
  initCampaignTracking: () => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const campaignData: Record<string, string> = {};
      let hasCampaign = false;

      // Standard UTMs
      [
        "utm_source",
        "utm_medium",
        "utm_campaign",
        "utm_term",
        "utm_content",
        "gclid",
        "fbclid",
      ].forEach((param) => {
        const val = urlParams.get(param);
        if (val) {
          campaignData[param] = val;
          hasCampaign = true;
        }
      });

      // Persist if found (Last Click Wins model)
      if (hasCampaign) {
        sessionStorage.setItem(
          "campaign_context",
          JSON.stringify(campaignData)
        );
      }

      // Retrieve for current session
      const stored = sessionStorage.getItem("campaign_context");
      if (stored) {
        const context = JSON.parse(stored);
        // Push campaign data as user properties or event parameters globally
        window.dataLayer.push({
          event: "campaign_identified",
          campaign_context: context,
          // GA4 expects these specifically for manual overriding if auto-tagging fails
          campaign_source: context.utm_source || "direct",
          campaign_medium: context.utm_medium || "none",
          campaign_name: context.utm_campaign || "(not set)",
          gclid: context.gclid || undefined,
        });
      }
    } catch (e) {
      console.warn("Campaign tracking init failed", e);
    }
  },

  // Contact Tracking
  trackContact: (type: "whatsapp" | "phone" | "email", label: string) => {
    window.dataLayer.push({
      event: "contact_click",
      contact_type: type,
      contact_label: label,
    });
  },

  // Navigation Tracking (SPA)
  trackNavigation: (destination: string) => {
    const pagePath = "/" + destination;
    const pageName = destination.charAt(0).toUpperCase() + destination.slice(1);

    window.dataLayer.push({
      event: "virtual_page_view",
      page_path: pagePath,
      page_title: pageName,
    });
  },

  // Internal Link Tracking
  trackInternalLink: (name: string, destination: string) => {
    window.dataLayer.push({
      event: "internal_link_click",
      link_name: name,
      link_destination: destination,
    });
  },
};
